import { ChevronDownIcon, Search } from 'lucide-react'
import { useState, type JSX } from 'react'
import logo from '../assets/minister.png' // Imported your emblem image asset

import {
  ChartIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  CogIcon,
  DocumentIcon,
  // GovernmentSealIcon,
  HomeIcon,
  MapIcon,
  RegulationIcon,
  ShieldIcon,
  UsersIcon,
  LockIcon
} from './icons'

interface SubItem {
  id: string
  label: string
}

interface NavItem {
  id: string
  label: string
  badge?: string
  children?: SubItem[]
}

interface SidebarProps {
  items: NavItem[]
  isExpanded: boolean
  isMobileOpen: boolean
  activeItemId: string
  onToggleExpand: () => void
  onCloseMobile: () => void
  onNavigate: (id: string) => void
}

type NavIcon = (props: { className?: string }) => JSX.Element

const navIcons: Record<string, NavIcon> = {
  dashboard: HomeIcon,
  users: UsersIcon,
  applications: RegulationIcon,
  approvals: ShieldIcon,
  reports: ChartIcon,
  regions: MapIcon,
  settings: CogIcon,
  locations: CogIcon,
  securityIcons: LockIcon
}

export function Sidebar({
  items,
  isExpanded,
  isMobileOpen,
  activeItemId,
  onToggleExpand,
  onCloseMobile,
  onNavigate,
}: SidebarProps) {
  // const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({
  //   'legal-advice-menu': false,
  //   'plo-court': false
  // })

  // Dynamic initialization: open the dropdown automatically if the active item lives inside it
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(() => {
    // 1. Look up which menu contains our currently active sub-item ID (e.g., 'form-1')
    const initialStates: Record<string, boolean> = {
      'legal-advice-menu': false,
      'plo-court': false
    };

    // 2. Scan through menu items to find where our active child is nesting
    items.forEach(item => {
      if (item.children?.some(child => child.id === activeItemId)) {
        initialStates[item.id] = true; // Auto-expand this specific parent group on mount!
      }
    });

    return initialStates;
  });

  const filteredItems = items.filter(item => item.id !== 'search')

  const toggleDropdown = (id: string) => {
    setOpenDropdowns((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleNavClick = (id: string, hasChildren: boolean) => {
    if (hasChildren) {
      toggleDropdown(id)
      if (!isExpanded) {
        onToggleExpand()
      }
    } else {
      onNavigate(id)
      onCloseMobile()
    }
  }

  // Helper to resolve icon by ID or fallback categories
const getNavIcon = (id: string): NavIcon => {
  // Direct match
  if (navIcons[id]) {
    return navIcons[id];
  }

  // Group security-related IDs to LockIcon
  if (id.includes('2fa') || id.includes('security') || id.includes('password')) {
    return navIcons.securityIcons; // LockIcon
  }

  // Fallback icon
  return RegulationIcon;
};

  const sidebarContent = (
    <>
      {/* Header section */}
<div
  className={`flex shrink-0 flex-col justify-center border-b border-blue-800/30 ${
    isExpanded ? 'h-32 px-4 py-3' : 'h-20 px-2'
  }`}
>
  <div className="flex w-full items-center justify-between">
    {isExpanded ? (
      /* Expanded View: Centered Stack Layout */
      <div className="flex w-full flex-col items-center text-center">
        {/* App Logo / Seal Image Top */}
        <img 
          src={logo} 
          alt="Logo" 
          className="h-20 w-20 object-contain"
        />
        {/* Title & Subtitle Under */}
        <div className="w-full leading-tight">
          <p className="text-xs font-semibold text-white tracking-wide">
            ဥပဒေရေးရာဝန်ကြီးဌာန
          </p>
          <p className="text-[10px] text-blue-200/70 mt-0.5 font-medium">
            Legal Information Management System
          </p>
        </div>
      </div>
    ) : (
      /* Collapsed View: Icon Only centered */
      <div className="flex w-full justify-center">
        <img 
          src= {logo}
          alt="Logo" 
          className="h-9 w-9 object-contain"
          title="ပြည်ထောင်စုရှေ့နေချုပ်ရုံး"
        />
      </div>
    )}

    {/* Mobile Close Button (Stays pinned to right on mobile layout screens) */}
    <button
      type="button"
      onClick={onCloseMobile}
      className="absolute right-4 top-4 rounded-lg p-1.5 text-blue-200 transition-colors hover:bg-blue-800 hover:text-white lg:hidden"
      aria-label="Close menu"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
</div>

      {/* Navigation & Search Area */}
      <div className="flex-1 overflow-y-auto px-2 py-4">
        
        {/* Perfectly Aligned Search Field Mock Button */}
        <div className="mb-1">
          {isExpanded ? (
            <button
              type="button"
              onClick={() => { onNavigate('search'); onCloseMobile(); }}
              className={`group relative flex w-full items-center rounded-lg border border-white/20 bg-transparent px-3 py-2.5 text-sm font-medium text-blue-100 transition-colors hover:border-white/40 hover:bg-white/10 hover:text-white select-none ${
                activeItemId === 'search' ? 'border-white/50 bg-white/15 text-white font-semibold' : ''
              }`}
            >
              <Search className={`h-5 w-5 shrink-0 text-blue-200 group-hover:text-white ${activeItemId === 'search' ? 'text-white' : ''}`} />
              <span className="ml-3 truncate text-left opacity-90">ရှာဖွေရန်</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { onNavigate('search'); onCloseMobile(); }}
              title="ရှာဖွေရန်"
              className={`flex w-full items-center justify-center rounded-lg border border-white/20 bg-transparent py-2.5 transition-colors hover:border-white/40 hover:bg-white/10 ${
                activeItemId === 'search' ? 'border-white/50 bg-white/15 text-white' : 'text-blue-100'
              }`}
            >
              <Search className="h-5 w-5 text-blue-200 group-hover:text-white" />
            </button>
          )}
        </div>

        {/* Core Navigation List */}
        <nav aria-label="Main navigation">
          <ul className="space-y-1">
            {filteredItems.map((item) => {
              const Icon = getNavIcon(item.id);
              const SubIcon = DocumentIcon
              const hasChildren = !!item.children?.length
              const isDropdownOpen = openDropdowns[item.id] && isExpanded
              
              const isActive = item.id === activeItemId
              const isChildActive = item.children?.some(child => child.id === activeItemId)

              return (
                <li key={item.id} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => handleNavClick(item.id, hasChildren)}
                    title={!isExpanded ? item.label : undefined}
                    className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive || (isChildActive && !isDropdownOpen)
                        ? 'bg-white/15 text-white font-semibold'
                        : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    } ${!isExpanded ? 'justify-center px-2' : ''}`}
                  >
                    <Icon
                      className={`h-5 w-5 shrink-0 ${
                        isActive || isChildActive ? 'text-white' : 'text-blue-200 group-hover:text-white'
                      }`}
                    />
                    
                    {isExpanded && (
                      <>
                        <span className="flex-1 truncate text-left">{item.label}</span>
                        {item.badge && (
                          <span className="rounded-md bg-blue-800/60 px-1.5 py-0.5 text-[10px] font-semibold text-blue-100">
                            {item.badge}
                          </span>
                        )}
                        {hasChildren && (
                          <ChevronDownIcon 
                            className={`h-4 w-4 text-blue-300 transition-transform duration-200 ${
                              isDropdownOpen ? 'rotate-180' : ''
                            }`} 
                          />
                        )}
                      </>
                    )}
                  </button>

                  {/* Sub-menu Dropdown rendering */}
                  {hasChildren && isDropdownOpen && (
                    <ul className="pl-8 pr-1 space-y-1 transition-all duration-200">
                      {item.children?.map((child) => {
                        const isSubActive = child.id === activeItemId
                        return (
                          <li key={child.id}>
                            <button
                              type="button"
                              onClick={() => {
                                onNavigate(child.id)
                                onCloseMobile()
                              }}
                              className={`flex w-full items-center rounded-lg py-2 pl-3 pr-2 text-xs font-medium transition-colors ${
                                isSubActive
                                  ? 'bg-white/10 text-white font-semibold'
                                  : 'text-blue-200/90 hover:bg-white/5 hover:text-white'
                              }`}
                            >
                              <SubIcon
                                className={`h-3 w-3 shrink-0 ${
                                  isActive || isChildActive ? 'text-white/50' : 'text-blue-200 group-hover:text-white/50'
                                }`}
                              />
                               {/* <span className="rounded-md bg-blue-800/60 px-1.5 py-0.5 text-[10px] font-semibold text-blue-100">
                            {item.badge}
                          </span> */}
                              <span className="flex-1 truncate px-1.5 py-0.5 text-left">{child.label}</span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        </nav>
      </div>

      {/* Collapse Action Footer */}
      <div className="hidden shrink-0 border-t border-blue-800/30 p-2 lg:block">
        <button
          type="button"
          onClick={onToggleExpand}
          className={`flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium text-blue-200 transition-colors hover:bg-white/10 hover:text-white ${
            isExpanded ? 'gap-3' : 'justify-center px-2'
          }`}
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isExpanded ? (
            <>
              <ChevronLeftIcon className="h-5 w-5 shrink-0" />
              <span></span>
            </>
          ) : (
            <ChevronRightIcon className="h-5 w-5 shrink-0" />
          )}
        </button>
      </div>
    </>
  )

  return (
    <>
      {isMobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-blue-900 shadow-xl transition-[transform,width] duration-300 ease-in-out lg:static lg:z-auto lg:shrink-0 lg:translate-x-0 lg:shadow-none ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isExpanded ? 'lg:w-64' : 'lg:w-[72px]'}`}
      >
        {sidebarContent}
      </aside>
    </>
  )
}